/* eslint-disable @typescript-eslint/naming-convention */
import type { BuildingGraphFaceOutwardSideDirection, BuildingGraphPlaneType } from "../building/buildingGraphTypes";
import type { RoomClass, RoomEnclosure, RoomType } from "../rooms/roomTypesEnum";
import type { FilterEntitySourceId } from "./entitySource";

export type PropertyKind = "SCALAR" | "SET";

export type PropertyType =
  | "string"
  | "number"
  | "boolean"
  | "id"
  | "enum"
  | "roomHandle"
  | "levelHandle"
  | "floorHandle"
  | "roofHandle"
  | "roomEdgeHandle"
  | "freeformWallHandle"
  | "freeformWallEdgeHandle";

export interface PropertyDescriptor {
  readonly kind: PropertyKind;
  readonly type: PropertyType;
  readonly values?: readonly (string | number)[];
}

export type WallPlaneSideKind = "EXTERIOR" | "ROOM_FACE" | "FLOOR_FACE" | "ROOF_FACE" | "FREEFORM_WALL_FACE";
export type PlanarTopologyFaceNodeType = "ROOM" | "FLOOR" | "ROOF" | "VOLUME_EDGE" | "UNKNOWN";

export const BUILDING_GRAPH_PLANE_TYPES: readonly BuildingGraphPlaneType[] = ["VERTICAL", "HORIZONTAL", "OTHER"];
export const BUILDING_GRAPH_FACE_OUTWARD_SIDE_DIRECTIONS: readonly BuildingGraphFaceOutwardSideDirection[] = [
  "WALL_PLANE_POSITIVE_NORMAL",
  "WALL_PLANE_NEGATIVE_NORMAL",
];
export const PLANAR_TOPOLOGY_FACE_NODE_TYPES: readonly PlanarTopologyFaceNodeType[] = [
  "ROOM",
  "FLOOR",
  "ROOF",
  "VOLUME_EDGE",
  "UNKNOWN",
];

export interface PropertyCatalog {
  WALL_PLANE: {
    planeHash: { kind: "SCALAR"; type: "string" };
    planeTopologyIds: { kind: "SET"; type: "id" };
    normalToZAxisAngle: { kind: "SCALAR"; type: "number" };
    planeType: { kind: "SCALAR"; type: "enum"; values: readonly BuildingGraphPlaneType[] };
    faceNodeIds: { kind: "SET"; type: "id" };
    planarGraphFaceCount: { kind: "SCALAR"; type: "number" };
    exteriorPlanarGraphFaceCount: { kind: "SCALAR"; type: "number" };
    hasExteriorPlanarGraphFace: { kind: "SCALAR"; type: "boolean" };
    incidentFaceEdgeIndices: { kind: "SET"; type: "number" };
  };
  FACE_NODE: {
    faceNodeId: { kind: "SCALAR"; type: "id" };
    /** Only set for face nodes whose parent handle is a VOLUME. */
    volumeNodeId: { kind: "SCALAR"; type: "id" };
    /** Only set for face nodes whose parent handle is a VOLUME. */
    volumeNodeFaceIndex: { kind: "SCALAR"; type: "number" };
    normalToZAxisAngle: { kind: "SCALAR"; type: "number" };
    outwardSideDirection: {
      kind: "SCALAR";
      type: "enum";
      values: readonly BuildingGraphFaceOutwardSideDirection[];
    };
    planeHash: { kind: "SCALAR"; type: "string" };
    isPerimeter: { kind: "SCALAR"; type: "boolean" };
    isFreeform: { kind: "SCALAR"; type: "boolean" };
    kinds: { kind: "SET"; type: "enum"; values: readonly WallPlaneSideKind[] };
    roomHandle: { kind: "SCALAR"; type: "roomHandle" };
    roomEdgeHandle: { kind: "SCALAR"; type: "roomEdgeHandle" };
    sourceRoomHandle: { kind: "SCALAR"; type: "roomHandle" };
    levelHandle: { kind: "SCALAR"; type: "levelHandle" };
    roomClass: { kind: "SCALAR"; type: "enum"; values: readonly RoomClass[] };
    roomEnclosure: { kind: "SCALAR"; type: "enum"; values: readonly RoomEnclosure[] };
    roomType: { kind: "SCALAR"; type: "enum"; values: readonly RoomType[] };
    wallPlaneHash: { kind: "SCALAR"; type: "string" };
    topologyFaceIds: { kind: "SET"; type: "id" };
    floorHandle: { kind: "SCALAR"; type: "floorHandle" };
    roofHandle: { kind: "SCALAR"; type: "roofHandle" };
    freeformWallHandle: { kind: "SCALAR"; type: "freeformWallHandle" };
    freeformWallEdgeHandle: { kind: "SCALAR"; type: "freeformWallEdgeHandle" };
  };
  FACE_EDGE: {
    faceEdgeIndex: { kind: "SCALAR"; type: "number" };
    faceEdgeType: { kind: "SCALAR"; type: "enum"; values: readonly ["OVERLAP", "EDGE"] };
    faceEdgeDot: { kind: "SCALAR"; type: "number" };
    faceANodeId: { kind: "SCALAR"; type: "id" };
    faceBNodeId: { kind: "SCALAR"; type: "id" };
    planeHashes: { kind: "SET"; type: "string" };
  };
  VOLUME_NODE: {
    volumeNodeId: { kind: "SCALAR"; type: "id" };
    buildingPlanarGraphId: { kind: "SCALAR"; type: "string" };
    buildingPlanarGraphFaceIndex: { kind: "SCALAR"; type: "number" };
    planeHashes: { kind: "SET"; type: "string" };
    roomHandle: { kind: "SCALAR"; type: "roomHandle" };
    sourceRoomHandle: { kind: "SCALAR"; type: "roomHandle" };
    levelHandle: { kind: "SCALAR"; type: "levelHandle" };
    floorHandle: { kind: "SCALAR"; type: "floorHandle" };
    roofHandle: { kind: "SCALAR"; type: "roofHandle" };
    roomClass: { kind: "SCALAR"; type: "enum"; values: readonly RoomClass[] };
    roomEnclosure: { kind: "SCALAR"; type: "enum"; values: readonly RoomEnclosure[] };
    roomType: { kind: "SCALAR"; type: "enum"; values: readonly RoomType[] };
    roomEdgeHandles: { kind: "SET"; type: "roomEdgeHandle" };
    faceNodeIds: { kind: "SET"; type: "id" };
    wallPlaneHashes: { kind: "SET"; type: "string" };
    topologyFaceIds: { kind: "SET"; type: "id" };
  };
  VOLUME_EDGE: {
    volumeEdgeId: { kind: "SCALAR"; type: "id" };
    volumeANodeId: { kind: "SCALAR"; type: "id" };
    volumeBNodeId: { kind: "SCALAR"; type: "id" };
    planeHashes: { kind: "SET"; type: "string" };
  };
  PLANAR_TOPOLOGY_FACE: {
    topologyId: { kind: "SCALAR"; type: "id" };
    faceIndex: { kind: "SCALAR"; type: "number" };
    planeHash: { kind: "SCALAR"; type: "string" };
    normalToZAxisAngle: { kind: "SCALAR"; type: "number" };
    planeType: { kind: "SCALAR"; type: "enum"; values: readonly BuildingGraphPlaneType[] };
    isUnbounded: { kind: "SCALAR"; type: "boolean" };
    hasAnyFaceEdge: { kind: "SCALAR"; type: "boolean" };
    isPerimeter: { kind: "SCALAR"; type: "boolean" };
    faceNodeIds: { kind: "SET"; type: "id" };
    faceNodeTypes: { kind: "SET"; type: "enum"; values: readonly PlanarTopologyFaceNodeType[] };
  };
}

export type PropertyPath<T extends FilterEntitySourceId> = Extract<keyof PropertyCatalog[T], string>;
